import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Container,
  Alert,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import {
  Location,
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
} from '../services/locations.service';

interface LocationFormData {
  name: string;
  address: string;
  phone: string;
}

const LocationsPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);

  // API hooks
  const { data: locationsResponse, isLoading, error } = useGetLocationsQuery({ active: true });
  const [createLocation, { isLoading: isCreating }] = useCreateLocationMutation();
  const [updateLocation, { isLoading: isUpdating }] = useUpdateLocationMutation();
  const [deleteLocation, { isLoading: isDeleting }] = useDeleteLocationMutation();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<LocationFormData>({
    defaultValues: {
      name: '',
      address: '',
      phone: '',
    },
  });

  const locations = locationsResponse?.data || [];

  const handleOpenDialog = (location?: Location) => {
    if (location) {
      setEditingLocation(location);
      reset({
        name: location.name,
        address: location.address,
        phone: location.phone || '',
      });
    } else {
      setEditingLocation(null);
      reset();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLocation(null);
    reset();
  };

  const onSubmit = async (data: LocationFormData) => {
    try {
      if (editingLocation) {
        // Actualizar ubicación existente
        await updateLocation({
          id: editingLocation.id,
          data: data
        }).unwrap();
      } else {
        // Crear nueva ubicación
        await createLocation(data).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar ubicación:', error);
    }
  };

  const handleDeleteClick = (location: Location) => {
    setLocationToDelete(location);
    setDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!locationToDelete) return;
    
    try {
      await deleteLocation(locationToDelete.id).unwrap();
      setDeleteDialog(false);
      setLocationToDelete(null);
    } catch (error) {
      console.error('Error al eliminar ubicación:', error);
    }
  };

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Error cargando ubicaciones</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            <StoreIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Gestión de Ubicaciones
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="large"
          >
            Nueva Ubicación
          </Button>
        </Box>

        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Administra las diferentes sucursales y puntos de venta
        </Typography>

        {isLoading ? (
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="80%" />
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Skeleton variant="text" width={80} />
                      <Skeleton variant="text" width={100} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {locations.map((location) => (
              <Grid key={location.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div" fontWeight="bold">
                        {location.name}
                      </Typography>
                      <Box sx={{ ml: 'auto' }}>
                        <Chip 
                          label={location.isActive ? 'Activa' : 'Inactiva'} 
                          color={location.isActive ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {location.address}
                    </Typography>
                    
                    {location.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PhoneIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {location.phone}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Estadísticas reales */}
                    {location._count && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            {location._count.users} usuarios
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <InventoryIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            {location._count.inventories} productos
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {location.excelFile && (
                      <Box sx={{ mt: 2 }}>
                        <Chip
                          label={`Migrado de: ${location.excelFile}`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Creada: {new Date(location.createdAt).toLocaleDateString()}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(location)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(location)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {locations.length === 0 && !isLoading && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <LocationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No hay ubicaciones configuradas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Comienza agregando tu primera sucursal o punto de venta
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Crear Primera Ubicación
            </Button>
          </Paper>
        )}

        {/* Dialog para crear/editar ubicación */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
              {editingLocation ? 'Editar Ubicación' : 'Nueva Ubicación'}
            </DialogTitle>
            
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'El nombre es requerido' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Nombre de la Ubicación"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        placeholder="Ej: Sucursal Centro, Almacén Principal"
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="address"
                    control={control}
                    rules={{ required: 'La dirección es requerida' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Dirección Completa"
                        multiline
                        rows={3}
                        error={!!errors.address}
                        helperText={errors.address?.message}
                        placeholder="Incluye barrio, colonia, ciudad y departamento"
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Teléfono (Opcional)"
                        placeholder="Ej: 2785-1234"
                      />
                    )}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="info.dark">
                  <strong>Nota:</strong> Una vez creada la ubicación, podrás asignar usuarios 
                  y configurar el inventario específico para esta sucursal.
                </Typography>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={isCreating || isUpdating}
              >
                {isCreating || isUpdating ? 'Guardando...' : (editingLocation ? 'Actualizar' : 'Crear')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Dialog de confirmación de eliminación */}
        <Dialog
          open={deleteDialog}
          onClose={() => setDeleteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirmar Eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de que deseas eliminar la ubicación <strong>{locationToDelete?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Esta acción no se puede deshacer y la ubicación será marcada como inactiva.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default LocationsPage;